import { useCallback, useEffect } from 'react';
import { ApolloClient, gql, MutationHookOptions, useApolloClient } from '@apollo/client';
import { useMutation } from 'platform/useMutation';
import {
  SignHelloSignContract,
  SignHelloSignContractVariables,
} from 'sellDirect/graphql/__generated__/SignHelloSignContract';
import { useObservability } from 'shared/helpers/observability';
import {
  ClosingExperienceTasksFragment,
  HelloSignGeneralAddendaKeyName,
  HelloSignTaskKeyName,
  improveHelloSignTask,
} from 'sellDirect/helpers/closingTasks';
import { HelloSignTaskFields_signers } from 'sellDirect/helpers/__generated__/HelloSignTaskFields';

const SignHelloSignContractMutation = gql`
  mutation SignHelloSignContract($signatureId: String!) {
    seller {
      signHelloSignContract(signatureId: $signatureId)
    }
  }
`;

export enum SignContractResult {
  /** Success and all signatories have signed */
  success_fully_signed = 'success_fully_signed',
  /** Success but there are signatories that still need to sign */
  success_not_fully_signed = 'success_not_fully_signed',
  /** There was an error */
  error = 'error',
}

/** Hook to handle signing a hello sign contract */
export const useSignContract = (options?: MutationHookOptions<SignHelloSignContract>) => {
  const { trackError, trackEvent } = useObservability();
  const apolloClient = useApolloClient();

  const [mutate, { data, loading, error }] = useMutation<
    SignHelloSignContract,
    SignHelloSignContractVariables
  >(SignHelloSignContractMutation, {
    awaitRefetchQueries: true,
    fetchPolicy: 'no-cache',
    refetchQueries: () => ['GetSellDirect'],
    onError: options?.onError,
  });

  const signContract = useCallback(
    async (
      signatureId: string,
      offerId: string,
      helloSignTaskName: HelloSignTaskKeyName | HelloSignGeneralAddendaKeyName,
      addendumId?: string,
    ) => {
      try {
        trackEvent('click', 'sign-contract', { signatureId });
        await mutate({ variables: { signatureId } });

        // Query the latest signers for this helloSignTaskKey
        const updatedSigners = getUpdatedSigners(
          apolloClient,
          offerId,
          helloSignTaskName,
          addendumId,
        );

        // The task is complete if all signers have signed the contract/addendum
        const isTaskComplete = updatedSigners.every((signer) => signer.signedAt);

        if (isTaskComplete) {
          return SignContractResult.success_fully_signed;
        }
        return SignContractResult.success_not_fully_signed;
      } catch (error) {
        const errorToTrack =
          error instanceof Error
            ? error
            : new Error('An unknown error happened while signing a contract.');
        trackError('sign-contract', errorToTrack, { signatureId });
        return SignContractResult.error;
      }
    },
    // trackError does not maintain the same identity across re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutate, apolloClient],
  );

  useEffect(() => {
    if (error) {
      trackError('sign-contract-unexpected-error', error);
    }
    // trackError doesn't maintain the same identity across re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return { signContract, loading, data, error };
};

/**
 * @param apolloClient - The apollo client
 * @param offerId - The ID for our offer
 * @param helloSignTaskName - The task key name for the hello sign task.
 * @param addendumId - Optional. The ID for the general addendum from the `signFormAddendums` task
 *
 * @returns The latest list of signers for the helloSignTaskKey task
 *
 * @throws Error if the latest task for helloSignTaskKey has invalid data
 */
const getUpdatedSigners = (
  apolloClient: ApolloClient<unknown>,
  offerId: string,
  helloSignTaskName: HelloSignTaskKeyName | HelloSignGeneralAddendaKeyName,
  addendumId?: string,
): HelloSignTaskFields_signers[] => {
  // Get latests tasks from the cache
  const offer = apolloClient.readFragment({
    id: `OdProtosWebData_Offer:${offerId}`,
    fragment: gql`
      fragment OfferClosingExperience on OdProtosWebData_Offer {
        id
        closingExperience {
          ...ClosingExperienceTasksFields
        }
      }
      ${ClosingExperienceTasksFragment}
    `,
    fragmentName: 'OfferClosingExperience',
  });

  // Get the task matching the `helloSignTaskName`
  const updatedTask = offer.closingExperience?.tasks?.[helloSignTaskName];

  // If the task is an array of tasks (i.e. signFormAddendums), find the task that matches the addendumId
  // Otherwise just return the task itself
  const helloSignTask =
    updatedTask instanceof Array
      ? updatedTask.find((task) => task?.id === addendumId)
      : updatedTask;

  // Clean up the raw hello sign task
  const improvedHelloSignTask = improveHelloSignTask(helloSignTask);

  if (!improvedHelloSignTask) {
    throw Error(
      `getUpdatedSigners expects the cache for GetClosingExperienceTasks to contain task ${helloSignTaskName} with a valid list of signers`,
    );
  }

  return improvedHelloSignTask.taskable.signers;
};
